import { Command, SearchInput } from '../input';
import { Dialog, DialogWindow, useDialogManager } from '../dialog';
import { Link } from '../buttons';
import React, { Suspense, useEffect, useRef, useState } from 'react';
import { NotificationBanner } from 'src/components/widgets/notificationBanner';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';
import { CustomErrorBoundary } from '../customErrorBoundary';
import { Icon } from '../icon';
import { DialogResults } from './DialogResults';
import { QuickSearchDialogResults } from './quickSearchDialogResults';
import { addRecentSearch, getRecentSearches } from './recentSearches';
import HistoryIcon from 'src/shared/material_icons/action/history.svg';
import { SupportTicketDropdownTrigger } from '../supportTicketDropdown';

const CustomSearchInput = styled(SearchInput)`
    width: 100%;
    border: none;
    border-bottom: 1px #ddd solid;
    border-radius: 0;
    padding: 20px 30px;
    height: auto;
    flex: 0;
    outline: none;

    &:focus {
        outline: none;
        border: none;
        border-bottom: 1px #ddd solid;
    }
`;

const SearchHelpRow = styled.div`
    display: flex;
    justify-content: center;
    padding: 2px 20px 4px;

    ${Link} {
        font-size: 12px;
    }
`;

// Create a empty state for the dialog
const GrayText = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    color: #666;
`;

const RecentSearches = styled.div`
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    padding: 10px 0;
`;

const RecentSearchesTitle = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #999;
    padding: 8px 30px;
`;

const RecentSearchItem = styled.button`
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    background: none;
    border: none;
    text-align: left;
    padding: 10px 30px;
    font-size: 14px;
    font-family: inherit;
    color: #333;
    cursor: pointer;

    &:hover {
        background: #f5f5f5;
    }

    ${Icon} {
        color: #999;
        flex-shrink: 0;
    }
`;

const DialogFooter = styled.div`
    display: flex;
    flex: 1;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 10px 20px;
`;

const FooterSeparator = styled.span`
    color: #999;
    font-size: 12px;
    line-height: 1;
`;

const FooterLink = styled(Link)`
    font-size: 12px;
    background: none;
    border: none;
    font-family: inherit;
`;

const EscCommand = styled(Command)`
    cursor: pointer;
`;

const CustomWindow = styled(DialogWindow)`
    width: 600px;
    height: 100%;
    padding: 0;
    overflow: hidden;
`;

const DialogContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    height: 100%;
`;

interface QuickSearchDialogProps {
    /** When set, the results include an AI Mode section that opens the sidebar on a new conversation about the search. */
    onAskAiMode?: (prompt: string) => void;
}

export default function QuickSearchDialog({ onAskAiMode }: QuickSearchDialogProps) {
    const { closeDialog, currentDialogId } = useDialogManager();
    const [filter, setFilter] = useState('');
    const [recentSearches, setRecentSearches] = useState<string[]>([]);
    const filterRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        filterRef.current?.focus();
    }, []);

    // Refresh the recent searches list whenever we return to the empty state.
    useEffect(() => {
        if (filter === '') {
            setRecentSearches(getRecentSearches());
        }
    }, [filter]);

    // Save the searched term shortly after the user stops typing, regardless of whether a result is opened.
    useEffect(() => {
        const term = filter.trim();
        if (!term) return;
        const timeout = setTimeout(() => addRecentSearch(term), 800);
        return () => clearTimeout(timeout);
    }, [filter]);

    // Close with ESC key
    useEffect(() => {
        const handleEsc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                closeDialog(currentDialogId);
            }
        };
        document.addEventListener('keydown', handleEsc);
        return () => {
            document.removeEventListener('keydown', handleEsc);
        };
    }, []);

    return (
        <Dialog
            windowDecorator={CustomWindow}
            showCloseButton={false}
            closeOnOverlayClick
            footerContent={
                <DialogFooter>
                    <FooterLink href={'https://zakeke.zendesk.com/hc/'} target='_blank' data-track='quick search knowlage base'>
                        {T._('Knowledge base', 'Admin')}
                    </FooterLink>
                    <FooterSeparator aria-hidden='true'>•</FooterSeparator>
                    <SupportTicketDropdownTrigger>
                        {({ onClick, isOpen }) => (
                            <FooterLink as='button' type='button' data-track='quick search leave message' aria-expanded={isOpen} onClick={onClick}>
                                {T._('Leave us a message', 'Admin')}
                            </FooterLink>
                        )}
                    </SupportTicketDropdownTrigger>
                </DialogFooter>
            }
        >
            <DialogContent>
                <CustomSearchInput
                    data-track='quick search input focus'
                    ref={filterRef}
                    type='text'
                    placeholder={T._('Quick search...', 'Admin')}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    rightElement={
                        <EscCommand
                            data-track='quick search esc command'
                            onClick={() => {
                                closeDialog(currentDialogId);
                            }}
                        >
                            ESC
                        </EscCommand>
                    }
                />

                <SearchHelpRow>
                    <Link
                        data-track='quick search learn more'
                        href={'https://zakeke.zendesk.com/hc/en-us/articles/12634522039836-How-to-search-Tools-Setting-and-more'}
                        target='_blank'
                    >
                        {T._('Learn more about search', 'Admin')}
                    </Link>
                </SearchHelpRow>

                {filter != '' && (
                    <Suspense>
                        <DialogResults>
                            <CustomErrorBoundary render={() => <NotificationBanner type='ERROR' content={T._('Error fetching search results.', 'Admin')} />}>
                                <QuickSearchDialogResults
                                    filter={filter}
                                    onAskAiMode={
                                        onAskAiMode
                                            ? (prompt) => {
                                                  closeDialog(currentDialogId);
                                                  onAskAiMode(prompt);
                                              }
                                            : undefined
                                    }
                                />
                            </CustomErrorBoundary>
                        </DialogResults>
                    </Suspense>
                )}

                {filter === '' && recentSearches.length > 0 && (
                    <RecentSearches>
                        <RecentSearchesTitle>{T._('Recent searches', 'Admin')}</RecentSearchesTitle>
                        {recentSearches.map((term) => (
                            <RecentSearchItem key={term} data-track='quick search recent search' onClick={() => setFilter(term)}>
                                <Icon>
                                    <HistoryIcon />
                                </Icon>
                                {term}
                            </RecentSearchItem>
                        ))}
                    </RecentSearches>
                )}

                {filter === '' && recentSearches.length === 0 && (
                    <GrayText>
                        <span>{T._('Start typing to search', 'Admin')}</span>
                    </GrayText>
                )}
            </DialogContent>
        </Dialog>
    );
}

export type DialogResultItem = {
    name: string;
    url: string;
    icon: string;
};
